import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { useAuth } from '../app/providers/AuthProvider';
import SEOHelmet from '../components/seo/SEOHelmet';
import { useFirebaseServices } from '../app/providers/FirebaseProvider';
import { useMeeting } from '../hooks/useMeeting';
import { useGroups } from '../features/groups/hooks/useGroups';
import {
  setRSVP,
  removeRSVP,
  subscribeToUserRSVP,
  subscribeToMeetingRSVPs,
  type RSVPStatus,
  type RSVP,
} from '../features/meetings/api/rsvpRepository';
import { updateMeetingStatus } from '../features/meetings/api/meetingRepository';
import {
  subscribeToMeetingNotifications,
  unsubscribeFromMeetingNotifications,
  isMeetingSubscribed,
} from '../features/notifications/api/subscriptionRepository';
import { usePushNotifications } from '../hooks/usePushNotifications';
import Breadcrumbs from '../components/navigation/Breadcrumbs';
import { useMeetingBreadcrumbs } from '../components/navigation/useBreadcrumbs';

// Import new subcomponents
import EventHero from '../features/meetings/components/EventHero';
import EventActions from '../features/meetings/components/EventActions';
import EventAgenda from '../features/meetings/components/EventAgenda';
import RSVPManagementPanel from '../features/meetings/components/RSVPManagementPanel';
import { useRSVPManagement } from '../features/meetings/hooks/useRSVPManagement';

const MeetingDetailPage = () => {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const { firestore } = useFirebaseServices();
  const { user, profile } = useAuth();

  // Real data fetching
  const {
    meeting,
    loading: meetingLoading,
    error: meetingError,
  } = useMeeting(meetingId || null);
  const { groups } = useGroups();

  const [rsvp, setRsvp] = useState<RSVP | null>(null);
  const [rsvpStatus, setRsvpStatus] = useState<RSVPStatus | null>(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [meetingRSVPs, setMeetingRSVPs] = useState<RSVP[]>([]);
  const [isMeetingNotificationSubscribed, setIsMeetingNotificationSubscribed] =
    useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);

  const { isSupported: isPushSupported, permission } = usePushNotifications();

  // Get the group for this meeting
  const group = meeting ? groups.find(g => g.id === meeting.groupId) : null;

  // Calculate organizer status early for hook dependencies
  const isOrganizer = group ? profile?.uid === group.organizerUid : false;

  // RSVP Management (organizer only, when approval is required)
  const rsvpManagement = useRSVPManagement(
    firestore,
    meeting?.requiresApproval && isOrganizer ? meetingId || null : null,
    isOrganizer ? profile?.uid || null : null,
  );

  // Navigation handlers - moved to top level to avoid conditional hook calls
  const handleGroupClick = useCallback(() => {
    if (group) {
      navigate(`/groups/${group.id}`);
    }
  }, [group, navigate]);

  // Generate breadcrumbs
  const breadcrumbItems = useMeetingBreadcrumbs(
    meeting?.title,
    meeting?.id,
    group?.name,
    group?.id,
  );

  // Subscribe to user's RSVP (full object for approval status tracking)
  useEffect(() => {
    if (!meetingId || !user) {
      setRsvp(null);
      setRsvpStatus(null);
      return;
    }

    const unsubscribe = subscribeToUserRSVP(
      firestore,
      meetingId,
      user.uid,
      rsvpData => {
        setRsvp(rsvpData);
        setRsvpStatus(rsvpData?.status || null);
      },
      err => {
        console.error('Error subscribing to RSVP:', err);
      },
    );

    return () => unsubscribe();
  }, [firestore, meetingId, user]);

  // Subscribe to all RSVPs for this meeting to get accurate attendee count
  useEffect(() => {
    if (!meetingId) {
      setMeetingRSVPs([]);
      return;
    }

    const unsubscribe = subscribeToMeetingRSVPs(
      firestore,
      meetingId,
      rsvps => {
        setMeetingRSVPs(rsvps);
      },
      err => {
        console.error('Error subscribing to meeting RSVPs:', err);
      },
    );

    return () => unsubscribe();
  }, [firestore, meetingId]);

  // Check if user is subscribed to meeting notifications
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!meetingId || !user) {
        setIsMeetingNotificationSubscribed(false);
        return;
      }

      try {
        const subscribed = await isMeetingSubscribed(firestore, meetingId, user.uid);
        setIsMeetingNotificationSubscribed(subscribed);
      } catch (err) {
        console.error('Error checking meeting subscription status:', err);
      }
    };

    void checkSubscriptionStatus();
  }, [firestore, meetingId, user]);

  const handleRSVP = useCallback(
    async (status: RSVPStatus) => {
      if (!user || !meetingId || !meeting || !profile) {
        return;
      }

      setRsvpLoading(true);
      setError(null);

      try {
        // Check if user is organizer or admin (they should be auto-approved)
        const isOrganizerOrAdmin = isOrganizer || profile.isAdmin;

        if (status === 'not_attending' && meeting.requiresApproval) {
          // For meetings requiring approval: delete RSVP when user clicks "Not Attending"
          // This ensures they go through fresh approval if they change their mind later
          await removeRSVP(firestore, meetingId, user.uid);
        } else {
          // For other cases: create/update the RSVP normally
          // Organizers and admins bypass approval requirement
          await setRSVP(
            firestore,
            meetingId,
            user.uid,
            status,
            meeting.requiresApproval && !isOrganizerOrAdmin,
            {
              displayName: profile.displayName,
              email: profile.email,
              photoURL: profile.photoURL,
            },
          );
        }
      } catch (rsvpError) {
        const message =
          rsvpError instanceof Error ? rsvpError.message : 'Failed to update RSVP';
        setError(message);
      } finally {
        setRsvpLoading(false);
      }
    },
    [user, meetingId, meeting, profile, firestore, isOrganizer],
  );

  const handleToggleNotifications = useCallback(async () => {
    if (!user || !meetingId) {
      return;
    }

    setNotificationLoading(true);
    setError(null);

    try {
      if (isMeetingNotificationSubscribed) {
        // Unsubscribe from meeting notifications
        await unsubscribeFromMeetingNotifications(firestore, meetingId, user.uid);
        setIsMeetingNotificationSubscribed(false);
      } else {
        // Subscribe to meeting notifications
        await subscribeToMeetingNotifications(firestore, meetingId, user.uid);
        setIsMeetingNotificationSubscribed(true);
      }
    } catch (notificationError) {
      const message =
        notificationError instanceof Error
          ? notificationError.message
          : 'Failed to update notification subscription';
      setError(message);
    } finally {
      setNotificationLoading(false);
    }
  }, [user, meetingId, isMeetingNotificationSubscribed, firestore]);

  // Organizer action handlers
  const handleEditEvent = useCallback(() => {
    if (meetingId) {
      navigate(`/meetings/${meetingId}/edit`);
    }
  }, [meetingId, navigate]);

  const handleCancelEvent = useCallback(async () => {
    if (!meetingId || !user) {
      return;
    }

    // Confirm cancellation
    const confirmed = window.confirm(
      'Are you sure you want to cancel this event? This action cannot be undone.',
    );
    if (!confirmed) {
      return;
    }

    try {
      await updateMeetingStatus(firestore, meetingId, 'canceled');
      setError(null);
    } catch (cancelError) {
      const message =
        cancelError instanceof Error ? cancelError.message : 'Failed to cancel event';
      setError(message);
    }
  }, [meetingId, user, firestore]);

  const handleSendUpdate = useCallback(() => {
    if (meeting?.groupId) {
      // Navigate to broadcast page with pre-filled meeting scope
      navigate(
        `/broadcast?scope=meeting&groupId=${meeting.groupId}&meetingId=${meeting.id}`,
      );
    }
  }, [meeting, navigate]);

  if (meetingLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography color="text.secondary">Loading meeting details...</Typography>
      </Container>
    );
  }

  if (meetingError) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load meeting: {meetingError.message}
        </Alert>
        <Button onClick={() => navigate('/groups')} variant="contained">
          Back to Groups
        </Button>
      </Container>
    );
  }

  if (!meeting) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Meeting not found or has been removed.
        </Alert>
        <Button onClick={() => navigate('/groups')} variant="contained">
          Back to Groups
        </Button>
      </Container>
    );
  }

  // Calculate attendee count
  const attendingCount = meetingRSVPs.filter(rsvp => rsvp.status === 'attending').length;

  // Generate meta tags for social sharing
  const eventUrl = `https://events.penticton.dev/meetings/${meeting.id}`;
  const eventTitle = meeting.title;
  const eventDescription = meeting.description ||
    `Join us for ${meeting.title} ${meeting.location ? `at ${meeting.location}` : ''}`;
  const eventImage = meeting.posterUrl || 'https://events.penticton.dev/android-chrome-512x512.png';
  const eventKeywords = `${meeting.title}, penticton events, tech meetup, developer community${meeting.tags ? `, ${meeting.tags.join(', ')}` : ''}`;

  return (
    <>
      {/* SEO and Social Media Meta Tags */}
      <SEOHelmet
        title={eventTitle}
        description={eventDescription}
        image={eventImage}
        url={eventUrl}
        keywords={eventKeywords}
      />

      {/* Breadcrumbs */}
      {meeting && <Breadcrumbs items={breadcrumbItems} />}

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Error Alert */}
        {error && (
          <Container maxWidth="lg" sx={{ pt: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Container>
        )}

        {/* Event Hero Section */}
        <EventHero
          meeting={meeting}
          group={group || undefined}
          attendingCount={attendingCount}
          onGroupClick={handleGroupClick}
        />

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Stack spacing={4}>
            {/* Description */}
            {meeting.description && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    About This Event
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.6,
                    }}
                  >
                    {meeting.description}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Main Content Grid */}
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={4}
              sx={{ alignItems: 'flex-start' }}
            >
              {/* Left Column - Agenda & Details */}
              <Stack spacing={4} sx={{ flex: 1, minWidth: 0 }}>
                {/* Event Agenda */}
                <EventAgenda meeting={meeting} />

                {/* Organizer Actions */}
                {isOrganizer && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Organizer Actions
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Manage this event as the organizer
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={handleEditEvent}
                          fullWidth
                        >
                          Edit Event
                        </Button>
                        <Button
                          variant="outlined"
                          color="warning"
                          startIcon={<CancelIcon />}
                          onClick={handleCancelEvent}
                          disabled={meeting.status === 'canceled'}
                          fullWidth
                        >
                          {meeting.status === 'canceled'
                            ? 'Event Canceled'
                            : 'Cancel Event'}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<AnnouncementIcon />}
                          onClick={handleSendUpdate}
                          fullWidth
                        >
                          Send Update
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {/* RSVP Management (Organizer only, when approval required) */}
                {isOrganizer && meeting.requiresApproval && (
                  <RSVPManagementPanel
                    pendingRSVPs={rsvpManagement.pendingRSVPs}
                    approvedRSVPs={rsvpManagement.approvedRSVPs}
                    rejectedRSVPs={rsvpManagement.rejectedRSVPs}
                    loading={rsvpManagement.loading}
                    approvingIds={rsvpManagement.approvingIds}
                    rejectingIds={rsvpManagement.rejectingIds}
                    batchApproving={rsvpManagement.batchApproving}
                    error={rsvpManagement.error}
                    onApprove={rsvpManagement.handleApprove}
                    onReject={rsvpManagement.handleReject}
                    onBatchApprove={rsvpManagement.handleBatchApprove}
                    onClearError={rsvpManagement.clearError}
                  />
                )}
              </Stack>

              {/* Right Column - Actions */}
              <Box sx={{ width: { xs: '100%', lg: '350px' }, flexShrink: 0 }}>
                <Stack spacing={3}>
                  {/* Event Actions */}
                  <EventActions
                    meeting={meeting}
                    group={group || undefined}
                    user={profile}
                    rsvp={rsvp}
                    rsvpStatus={rsvpStatus}
                    rsvpLoading={rsvpLoading}
                    attendingCount={attendingCount}
                    isNotificationSubscribed={isMeetingNotificationSubscribed}
                    notificationLoading={notificationLoading}
                    isPushSupported={isPushSupported}
                    notificationPermission={permission}
                    onRSVP={handleRSVP}
                    onToggleNotifications={handleToggleNotifications}
                  />
                </Stack>
              </Box>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default MeetingDetailPage;
